/* MailBakes — choosing the postage on the product page.
   ---------------------------------------------------------------------------
   The brief: shipping is picked with the cake, not at checkout, and the cost
   belongs to that cake rather than to the order. That matters here more than
   it would anywhere else, because one MailBakes order is often several cakes
   going to several different addresses — and Shopify's checkout only has one
   shipping line for the whole order. It cannot describe "Tracked 24 to Mum and
   Special Delivery to a friend". Per card is the only honest way to charge it.

   How it works, in the same shape the flow already uses for extras:

     · the choice is written onto the card line as a visible property,
       `Delivery Option`, which the bakery's order app reads;
     · the money is a separate cart line — one variant of the hidden
       "Delivery" product — carrying `_mb_parent_group`, exactly like the
       slice upgrade and every extra.

   Prices come from that product at runtime (/products/mb-delivery.js), so a
   price change in Shopify is live here with no code change and no deploy.

   ---------------------------------------------------------------------------
   One thing to know before editing: this file adds the delivery line by
   watching the flow's own add-to-cart request rather than by being called from
   mb-flow.js. That is deliberate — it keeps a 31KB file that works today
   untouched — but it is the seam to tidy up first when someone next has
   mb-flow.js open. Fold the choice into its addToCart() and delete the wrapper.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var flow = document.querySelector('[data-mb-flow]');
  if (!flow) return;

  var PRODUCT = '/products/mb-delivery.js';

  /* The three services actually sold. Everything else on that product is a
     discounted version of one of these, applied in the basket once we know how
     many cards are going where — see isHeadline below. */
  var SERVICES = [
    {
      match: 'RM Tracked 24',
      blurb: 'Tracked the whole way. Usually arrives the next day.',
      note: 'Standard'
    },
    {
      match: 'Tracked 24 with Signature',
      blurb: 'Tracked delivery with a signature on arrival.',
      note: null
    },
    {
      match: 'Special Delivery',
      blurb: 'Guaranteed by 1pm tomorrow, signed for on the doorstep (Mondays-Saturdays).',
      note: null
    }
  ];

  /* A discounted variant has an em dash and the reason after it. The plain
     titles are the ones a customer chooses between. */
  function isHeadline(title) {
    return String(title).indexOf('—') === -1;
  }

  /* `chosen` is what goes in the basket; `picked` is whether the customer
     actually asked for it.

     These used to start apart: the first service was preselected so nobody
     could reach the basket without postage, but until it had been tapped it
     counted as £0 on the running total on the grounds that a default is not a
     decision. In practice that read as a bug - the option was visibly
     selected and its price was printed on the button, yet the total behind it
     said postage was free, and it only went up if you happened to tap the
     option that was already on. Whatever is selected is what gets charged, so
     it is what the total has to show from the first render.

     `picked` is kept rather than deleted because the basket and the add-to-
     cart wrapper both read it, and because it still records the difference
     between a default and a deliberate choice if that is ever wanted again.
     It simply no longer decides what the customer is told they will pay.

     `reached` is the one thing that does gate the total now, and it is a
     question of WHEN rather than WHETHER. Postage joins the running total
     when the customer arrives at the delivery step - the screen where the
     question is asked and the prices are on show - and not on the two
     screens before it, where a postage charge would be an unexplained £4 on
     a cake they have not finished choosing. From that point it counts
     whatever is selected, tapped or not.

     It is sticky: stepping back to the message does not take the postage off
     again, because by then it has been seen and explained. And it can never
     be false at the point it matters - the basket is only reachable from the
     step after this one. */
  var state = {
    allVariants: [], variants: [], chosen: null, picked: true, reached: false, currency: '£'
  };

  try {
    var blob = document.querySelector('[data-mb-flow-json]');
    if (blob) {
      var D = JSON.parse(blob.textContent);
      if (D && D.currency) state.currency = D.currency;
    }
  } catch (e) { /* the pound sign is a fine fallback */ }

  function money(pence) {
    return state.currency + (pence / 100).toFixed(2);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function serviceFor(title) {
    for (var i = 0; i < SERVICES.length; i++) {
      if (String(title) === SERVICES[i].match || (SERVICES[i].match === 'Special Delivery' && String(title).indexOf('Special Delivery') === 0)) return SERVICES[i];
    }
    return null;
  }

  /* ---------------------------------------------------------------- the UI */

  /* The travel block is the postage question: its heading and its line come
     from the section settings, so all that is added here is the options,
     underneath them. The date picker is not ours - Liquid puts #mb-calwrap
     inside the "when" block and mb-flow.js draws into it there. */
  function mount() {
    var host = flow.querySelector(
      '.mb-screen[data-step="5"] .mb-dblk--travel'
    );
    if (!host || document.getElementById('mb-ship')) return;

    var box = document.createElement('div');
    box.className = 'mb-ship';
    box.innerHTML = '<div class="mb-opts" id="mb-ship"></div>';
    host.appendChild(box);

    style();
    render();
    announce();
    watchArrival();
  }

  /* mb-flow.js shows a step by putting is-on on its section, so the arrival
     is a class change on the delivery screen. Watched rather than hooked
     into the flow's own step() - this file has stayed out of mb-flow.js on
     purpose, and one observer is a smaller price than a second seam between
     the two.

     Already on it when we mount is a real case: the picker only appears once
     the delivery product has been fetched, and a customer who knows what
     they want can be at the delivery step before that lands. */
  /* mb-flow-revisions.js splits step 5 into two halves - "When should it
     land?" then "How should it travel?" - by setting data-mbfr-sub on the
     .mb-dwrap and hiding the other half with display:none. Step 5 gaining
     is-on therefore means the WHEN half, where postage has not been asked
     about yet and must not already be in the total.

     So the arrival we care about is data-mbfr-sub becoming "travel", not the
     step becoming is-on. The attribute is watched rather than the class
     because it is the one authoritative expression of sub-step state and it
     is set before the visual change, not after.

     The attribute does not exist until the customer first reaches step 5, and
     it never exists at all when the revisions file is switched off. Hence the
     two-stage wait below: the class tells us step 5 is live, and a beat later
     the attribute tells us whether the split is in play. */
  function subWrap() {
    var screen = flow.querySelector('.mb-screen[data-step="5"]');
    return screen ? screen.querySelector('.mb-dwrap') : null;
  }

  function watchSub(wrap) {
    if (wrap.getAttribute('data-mbfr-sub') === 'travel') { arrive(); return; }
    var w = new MutationObserver(function () {
      if (wrap.getAttribute('data-mbfr-sub') !== 'travel') return;
      w.disconnect();
      arrive();
    });
    w.observe(wrap, { attributes: true, attributeFilter: ['data-mbfr-sub'] });
  }

  /* Step 5 is live. Give mb-flow-revisions.js a beat to stamp the attribute -
     both files react to the same class change, so which runs first is a race -
     then decide. No attribute means no split, which means the old behaviour is
     correct: the whole step is the delivery step. Falls toward counting
     postage rather than under-quoting, as before. */
  function onStepLive() {
    setTimeout(function () {
      var wrap = subWrap();
      if (wrap && wrap.getAttribute('data-mbfr-sub')) { watchSub(wrap); return; }
      arrive();
    }, 0);
  }

  function watchArrival() {
    var screen = flow.querySelector('.mb-screen[data-step="5"]');
    if (!screen) { arrive(); return; }
    if (typeof MutationObserver !== 'function') { arrive(); return; }

    /* Already past the split when we mounted - the picker only appears once
       the Delivery product has been fetched, and someone who knows what they
       want can be on the travel half before that lands. */
    var wrap = subWrap();
    if (wrap && wrap.getAttribute('data-mbfr-sub')) { watchSub(wrap); return; }

    if (screen.classList.contains('is-on')) { onStepLive(); return; }
    var watcher = new MutationObserver(function () {
      if (!screen.classList.contains('is-on')) return;
      watcher.disconnect();
      onStepLive();
    });
    watcher.observe(screen, { attributes: true, attributeFilter: ['class'] });
  }

  /* One way only. Anything that cannot tell us when the step is reached -
     no section, no MutationObserver - falls back to counting postage from
     the start, which is the honest failure: the total is never lower than
     what will be charged. */
  function arrive() {
    if (state.reached) return;
    state.reached = true;
    render();
    announce();
  }

  function style() {
    if (document.getElementById('mb-ship-css')) return;
    var css = document.createElement('style');
    css.id = 'mb-ship-css';
    css.textContent = [
      '.mb-opt__note{display:inline-block;margin-left:8px;font-size:11.5px;',
      'font-weight:700;letter-spacing:.04em;text-transform:uppercase;',
      'color:var(--mb-ink-60,#6E666A)}'
    ].join('');
    document.head.appendChild(css);
  }

  /* The standard service is the floor every other option is priced against.
     Cheapest headline variant rather than variants[0]: the admin's variant
     order is not a guarantee, and this number is now printed on the earlier
     screens as "plus postage", so it must not move if someone reorders the
     Delivery product. */
  function baseVariant() {
    var b = null;
    for (var i = 0; i < state.variants.length; i++) {
      if (!b || state.variants[i].price < b.price) b = state.variants[i];
    }
    return b;
  }

  var postageNote = flow.querySelector('.mb-dblk--travel .mb-dblk__s');
  var singlePostageNote = postageNote ? postageNote.textContent : '';
  function cardQty() {
    return Math.max(1, Math.min(10, parseInt(flow.getAttribute('data-mb-card-quantity'), 10) || 1));
  }
  function quote(v, n) {
    if (!v) return { variant: null, quantity: 0, price: 0 };
    if (flow.getAttribute('data-mb-address-mode') === 'separate') return { variant: v, quantity: n, price: v.price * n };
    if (n < 2) return { variant: v, quantity: 1, price: v.price };
    var title = String(v.title);
    var wanted = title + ' — Multi-Card ' + (n <= 5 ? '2-5' : '6-10');
    var chosen = state.allVariants.filter(function (x) { return x.title === wanted && x.available !== false; })[0] || v;
    var count = chosen !== v ? 1 : n;
    return { variant: chosen, quantity: count, price: chosen.price * count };
  }

  function render() {
    if (postageNote) postageNote.textContent = cardQty() > 1
      ? (flow.getAttribute('data-mb-address-mode') === 'separate' ? 'Each card travels separately. Prices below are the total postage for all cards.' : 'One address for all cards. Prices below cover the whole pack.')
      : singlePostageNote;
    var list = document.getElementById('mb-ship');
    if (!list) return;

    var base = baseVariant();

    list.innerHTML = state.variants.map(function (v) {
      var s = serviceFor(v.title) || {};
      var on = state.chosen && state.chosen.id === v.id ? ' is-on' : '';
      /* The flow's price bar totals the `data-ship-price` of the selected
         option straight off the DOM - one source of truth, so stepping back
         and forward cannot desync it. Once the delivery step has been
         reached every option carries its real price, default or not: the
         number on the button and the number it adds to the running total are
         the same one. Before then the attribute is 0, which is what keeps
         postage out of the total on the earlier screens. The price printed
         on the button is the real one throughout - it is the question being
         asked, so it is never hidden. */
      var quoted = quote(v, cardQty());
      var counts = state.reached ? quoted.price : 0;
      /* The standard rate is the flat rate everyone pays, so it shows its own
         price. Everything above it is an upgrade ON that rate, not a separate
         charge, so it shows the difference: "+£1.99" reads as a small step up
         where "£5.99" reads as a second postage bill. The running total still
         adds the option's real price - only the label changes. */
      var extra = base ? quoted.price - quote(base, cardQty()).price : 0;
      var label = cardQty() > 1 ? money(quoted.price) : (extra > 0 ? '+' + money(extra) : money(quoted.price));
      var note = s.note
        ? '<span class="mb-opt__note">' + esc(s.note) + '</span>'
        : '';
      return '<button type="button" class="mb-opt' + on + '"' +
        ' data-ship="' + v.id + '"' +
        ' data-ship-price="' + counts + '">' +
        '<span class="mb-opt__rd"></span><span>' +
        '<span class="mb-opt__t">' + esc(v.title) + note + '</span>' +
        '<span class="mb-opt__d" style="white-space: pre-line;">' + esc(s.blurb || '') + '</span></span>' +
        '<span class="mb-opt__p">' + label + '</span></button>';
    }).join('');
  }

  /* The flow's bottom bar totals what the customer pays, so it has to be
     told the moment postage changes. An event rather than a call, so neither
     file needs to know the other is there. */
  function announce() {
    var price = state.reached ? quote(state.chosen, cardQty()).price : 0;
    /* `base` is ungated on purpose. `price` is what postage adds to the total
       and stays 0 until the delivery step, exactly as before. `base` is the
       standard rate, sent as soon as it is known, so the flow's bottom bar can
       print "plus £4.00 postage" on the earlier screens without that £4.00
       being counted twice. */
    var b = baseVariant();
    var detail = { price: price, base: quote(b, cardQty()).price };
    var ev;
    try {
      ev = new CustomEvent('mb:postage', { detail: detail });
    } catch (e) {
      ev = document.createEvent('CustomEvent');
      ev.initCustomEvent('mb:postage', false, false, detail);
    }
    document.dispatchEvent(ev);
  }

  flow.addEventListener('click', function (e) {
    var t = e.target.closest('[data-ship]');
    if (!t) return;
    var id = parseInt(t.getAttribute('data-ship'), 10);
    state.chosen = state.variants.filter(function (v) { return v.id === id; })[0] || null;
    state.picked = true;
    render();
    announce();
  });

  /* ------------------------------------------------------- the cart request

     The flow adds the card first and its add-ons second. This watches for the
     card — the request carrying _mb_group — writes the chosen delivery onto
     it as a property, and then adds the matching Delivery variant as its own
     line. Anything else passes straight through untouched. */

  var nativeFetch = window.fetch;

  function isCartAdd(url) {
    return String(url).indexOf('/cart/add') > -1;
  }

  function addDeliveryLine(group, n) {
    if (!state.chosen || !group) return;
    var quoted = flow.getAttribute('data-mb-address-mode') === 'separate' ? { variant: state.chosen, quantity: 1 } : quote(state.chosen, n);
    return nativeFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{
          id: quoted.variant.id,
          quantity: quoted.quantity,
          properties: { _mb_parent_group: group }
        }]
      })
    }).catch(function () {
      /* Silent on purpose. A missing delivery line is caught in the basket,
         which checks every card has one before it will let you check out —
         far better than blocking an add-to-cart over a network blip. */
    });
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    if (!state.chosen || !init || String(init.method).toUpperCase() !== 'POST' ||
        !isCartAdd(url)) {
      return nativeFetch.apply(this, arguments);
    }

    var group = null, n = 1, groups = [];

    try {
      if (typeof FormData !== 'undefined' && init.body instanceof FormData) {
        group = init.body.get('properties[_mb_group]');
        n = Math.max(1, parseInt(init.body.get('quantity'), 10) || 1);
        if (group) { init.body.append('properties[Delivery Option]', state.chosen.title); groups.push({ group: group, quantity: n }); }
      } else if (typeof init.body === 'string') {
        var payload = JSON.parse(init.body);
        (payload && payload.items || []).forEach(function (item) {
          if (item.properties && item.properties._mb_group) {
            group = item.properties._mb_group;
            n = Math.max(1, parseInt(item.quantity, 10) || 1);
            item.properties['Delivery Option'] = state.chosen.title;
            groups.push({ group: group, quantity: n });
          }
        });
        if (groups.length) init.body = JSON.stringify(payload);
      }
    } catch (e) {
      group = null;      /* leave the request exactly as it was */
    }

    var result = nativeFetch.apply(this, arguments);
    if (!group) return result;

    return result.then(function (res) {
      if (res && res.ok) return groups.reduce(function (chain, item) { return chain.then(function () { return addDeliveryLine(item.group, item.quantity); }); }, Promise.resolve()).then(function () { return res; });
      return res;
    });
  };

  document.addEventListener('mb:multibuy:change', function () { render(); announce(); });

  /* -------------------------------------------------------------- the data */

  nativeFetch(PRODUCT, { headers: { Accept: 'application/json' } })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (p) {
      if (!p || !p.variants) return;
      state.allVariants = p.variants;
      state.variants = p.variants.filter(function (v) {
        return v.available !== false && isHeadline(v.title);
      });
      if (!state.variants.length) return;
      /* Cheapest, not first. Same variant as before in practice, but the
         default postage is now quoted on the earlier screens, so it must not
         change silently when someone reorders variants in the admin. */
      state.chosen = baseVariant();
      mount();
    })
    .catch(function () {
      /* No delivery product, no picker. The flow carries on exactly as it
         did before this file existed. */
    });
})();
